import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { revalidateLogic } from '@tanstack/react-form';
import { useState } from 'react';
import { useParams } from 'react-router';

import {
  type AudienceType,
  type AudienceUpdateType,
  mutateAudienceUpdate,
  useAudienceById,
} from '@/api/audience';
import AudienceDetailEditHubspot from '@/app/audiences/[audience]/edit/AudienceDetailEditHubspot';
import AudienceDetailEditSalesforce from '@/app/audiences/[audience]/edit/AudienceDetailEditSalesforce';
import AudienceBaseForm, { audienceBaseFormFields } from '@/app/audiences/AudienceBaseForm';
import { Button, LinkButton } from '@/components/buttons';
import { useAppForm } from '@/components/Form';
import { Panel } from '@/components/panels';
import { PendoPlaceholder } from '@/components/PendoPlaceholder';
import Prompt from '@/components/Prompt';
import { ReturnToLink } from '@/components/ReturnToLink';
import { toTanStackFormFieldErrors } from '@/error/tanstack-form-field-errors';
import { RuleBuilder, RuleBuilderPreviewDialog } from '@/features/RuleBuilder';
import { RULE_BUILDER_VERSION } from '@/features/RuleBuilder/constants';
import type { AudienceRuleFormValues } from '@/features/RuleBuilder/types';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

type Props = {
  audience: AudienceType;
};

const AudienceDetailEditContent = ({ audience }: Props) => {
  const { audience: audienceId } = useParams();
  const navigate = useInstanceNavigate();
  const { mutate } = useAudienceById(audienceId);
  const { removeAllNotifications, pushNotification } = useNotification();
  const [isUpdatingAudience, setIsUpdatingAudience] = useState<boolean>(false);
  const [previewRulesConfig, setPreviewRulesConfig] = useState<string | undefined>(undefined);

  const form = useAppForm({
    defaultValues: {
      name: audience?.name ?? '',
      description: audience?.description ?? '',
      version: audience?.rulesConfig?.version ?? RULE_BUILDER_VERSION,
      rules: audience?.rulesConfig?.rules ?? {
        dateRange: { unit: 'day' as const, count: 90 },
        orConditions: [],
      },
    } satisfies AudienceRuleFormValues,
    validationLogic: revalidateLogic(),
    validators: {
      onSubmitAsync: async ({ value }) => {
        if (!audience) {
          return;
        }

        setIsUpdatingAudience(true);

        try {
          const updatedAudience = {
            ...audience,
            ...value,
            rulesConfig: value.rules
              ? JSON.stringify({ rules: value.rules, version: value.version })
              : undefined,
          } as AudienceUpdateType;
          await mutateAudienceUpdate(updatedAudience);
          mutate({
            audiences: {
              getById: {
                ...audience,
                name: value.name,
                description: value.description,
                rulesConfig: value.rules
                  ? JSON.stringify({ rules: value.rules, version: value.version })
                  : undefined,
              },
            },
          });
          navigate(ROUTES.audiences.path, {
            state: {
              successMessage: t`Audience was successfully updated.`,
              optimisticUpdate: updatedAudience,
            },
          });
        } catch (error) {
          const fieldErrors = toTanStackFormFieldErrors(error);
          if (fieldErrors) {
            return fieldErrors;
          }

          pushNotification({
            type: 'error',
            message: t`An unknown error has occurred, please try again or contact support.`,
          });
        } finally {
          setIsUpdatingAudience(false);
        }
      },
    },
  });

  const handlePreview = async () => {
    const result = await form.validateAllFields('submit');
    const isValid = result.length === 0;

    if (isValid) {
      removeAllNotifications();
      const values = form.state.values;
      setPreviewRulesConfig(
        JSON.stringify({
          rules: values.rules,
          version: values.version,
        }),
      );
    } else {
      pushNotification({
        type: 'error',
        message: t`Please fix rule errors before previewing`,
      });
    }
  };

  return (
    <>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.AppForm>
          <form.Subscribe
            selector={(state) => ({
              isDirty: state.isDirty,
              isSubmitting: state.isSubmitting,
            })}
          >
            {({ isDirty, isSubmitting }) => (
              <Prompt
                message={t`Are you sure you want to exit without saving your changes?`}
                shouldPreventNavigation={isDirty && !isSubmitting}
              />
            )}
          </form.Subscribe>
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between gap-8 max-md:flex-col max-md:items-start max-md:gap-4">
              <ReturnToLink route={ROUTES.audiences.path} routeName={t`Audiences`} />
              <div className="flex items-center gap-4 max-md:w-full max-md:justify-end">
                <PendoPlaceholder />
                <LinkButton to={ROUTES.audiences.path}>
                  <Trans>Cancel</Trans>
                </LinkButton>
                {audience?.type === 'rule' && (
                  <Button onClick={handlePreview}>
                    <Trans>Preview</Trans>
                  </Button>
                )}
                <form.AppForm>
                  <form.SubmitButton variant="primary">
                    <Trans>Save</Trans>
                  </form.SubmitButton>
                </form.AppForm>
              </div>
            </div>
            <Panel title={<Trans>Edit Audience Details</Trans>}>
              {audience?.type === 'salesforce' && (
                <AudienceDetailEditSalesforce url={audience?.salesforce_url} />
              )}
              {audience?.type === 'hubspot' && (
                <AudienceDetailEditHubspot url={audience?.hubspot_url} />
              )}
              <AudienceBaseForm
                form={form}
                fields={audienceBaseFormFields}
                audience={audience}
                autoFocus={false}
              />
            </Panel>
            {audience?.type === 'rule' && (
              <Panel>
                <RuleBuilder disabled={isUpdatingAudience} />
              </Panel>
            )}
          </div>
        </form.AppForm>
      </form>
      <RuleBuilderPreviewDialog
        rulesConfig={previewRulesConfig}
        onClose={() => setPreviewRulesConfig(undefined)}
      />
    </>
  );
};

export default AudienceDetailEditContent;
